/**
 * PWA push notification service.
 * Requests permission, subscribes via service worker, and registers subscription with backend.
 *
 * Each device/session that grants permission and subscribes is registered separately
 * on the backend. The server then sends push to all registered devices for that user
 * (landlord) or tenant, so notifications appear on every logged-in device (laptop,
 * mobile PWA, etc.) that has enabled notifications.
 */

import { API_BASE_URL } from '../config/api';
import { apiService } from './api';
import { tenantService } from './tenantService';

export type NotificationPermission = 'default' | 'granted' | 'denied';

/** Persisted user choice so we don't re-prompt every load in the same browser. */
export type PushPermissionChoice = 'granted' | 'denied' | 'dismissed';

const STORAGE_KEY_CHOICE = 'tenantplus_push_choice';
const STORAGE_KEY_SUBSCRIPTION_FAILED = 'tenantplus_push_subscription_failed';

export function getStoredPushChoice(): PushPermissionChoice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY_CHOICE);
    if (v === 'granted' || v === 'denied' || v === 'dismissed') return v;
    return null;
  } catch {
    return null;
  }
}

export function setStoredPushChoice(choice: PushPermissionChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY_CHOICE, choice);
  } catch {
    // ignore
  }
}

export function getPushSubscriptionFailed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_SUBSCRIPTION_FAILED) === '1';
  } catch {
    return false;
  }
}

export function setPushSubscriptionFailed(failed: boolean): void {
  try {
    if (failed) {
      localStorage.setItem(STORAGE_KEY_SUBSCRIPTION_FAILED, '1');
      try {
        window.dispatchEvent(new CustomEvent('tenantplus_push_subscription_failed'));
      } catch {
        // ignore
      }
    } else {
      localStorage.removeItem(STORAGE_KEY_SUBSCRIPTION_FAILED);
    }
  } catch {
    // ignore
  }
}

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionJSON {
  endpoint: string;
  keys: PushSubscriptionKeys;
  expirationTime?: number | null;
}

/**
 * Get VAPID public key from backend (no auth required).
 */
export async function getVapidPublicKey(): Promise<string | null> {
  try {
    const base = API_BASE_URL.replace(/\/$/, '');
    const res = await fetch(`${base}/push/vapid-public-key`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.publicKey ?? null;
  } catch {
    return null;
  }
}

/**
 * Request browser notification permission.
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  const perm = await Notification.requestPermission();
  return perm as NotificationPermission;
}

/**
 * Get current notification permission.
 */
export function getPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission as NotificationPermission;
}

/**
 * Check if push is supported (service worker + PushManager).
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Detect if running as installed PWA (standalone) where SW may take longer to be ready. */
function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

/**
 * Get the active service worker registration.
 * In PWA standalone, the SW often isn't ready on first paint; we retry several times with delays.
 */
async function getSWRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const isPWA = isStandalonePWA();
  const maxAttempts = isPWA ? 5 : 2;
  const delayMs = isPWA ? 1200 : 800;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      let reg = await navigator.serviceWorker.ready;
      if (reg) return reg;
      // Prefer the registration that controls this page (more reliable in PWA)
      if (navigator.serviceWorker.controller) {
        const regFromGet = await navigator.serviceWorker.getRegistration();
        if (regFromGet) return regFromGet;
      }
    } catch {
      // ignore and retry
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return null;
}

/**
 * Convert Uint8Array to base64url (for applicationServerKey).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications. Requires permission granted and VAPID key from backend.
 * Returns the PushSubscription JSON for sending to backend, or null on failure.
 *
 * Strategy:
 *  1. Re-use an existing valid subscription rather than creating a new one (avoids
 *     AbortError caused by VAPID key mismatch on a stale subscription).
 *  2. If the existing subscription is incomplete/stale, unsubscribe it first.
 *  3. On AbortError or InvalidStateError (push-service rejection), clear any stale
 *     subscription and retry once — this covers the VAPID key rotation scenario.
 */
export async function subscribeToPush(): Promise<PushSubscriptionJSON | null> {
  if (!isPushSupported()) return null;
  const reg = await getSWRegistration();
  if (!reg) return null;

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    console.warn('[Push] VAPID public key not available');
    return null;
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidKey);

  // ── Step 1: check for an existing subscription and re-use if valid ──
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      const json = existing.toJSON();
      if (json.endpoint && json.keys?.p256dh && json.keys?.auth) {
        // Re-use the valid existing subscription (no new network round-trip to push service)
        return {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
          expirationTime: json.expirationTime ?? null,
        };
      }
      // Subscription object exists but is incomplete — unsubscribe before proceeding
      await existing.unsubscribe().catch(() => undefined);
    }
  } catch {
    // Ignore errors reading existing subscription; continue to fresh subscribe
  }

  // ── Step 2: subscribe (with one retry on push-service rejection) ──
  const doSubscribe = async (): Promise<PushSubscriptionJSON | null> => {
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    const json = sub.toJSON();
    return {
      endpoint: json.endpoint!,
      keys: { p256dh: json.keys!.p256dh!, auth: json.keys!.auth! },
      expirationTime: json.expirationTime ?? null,
    };
  };

  try {
    return await doSubscribe();
  } catch (err: any) {
    // AbortError / InvalidStateError → push service rejected the request.
    // Most common cause: a stale subscription tied to a different VAPID key exists
    // in the browser's internal store but wasn't returned by getSubscription() above.
    // Clearing it and retrying usually resolves the issue.
    if (err?.name === 'AbortError' || err?.name === 'InvalidStateError') {
      console.warn('[Push] Subscribe failed with', err.name, '— clearing stale subscription and retrying…');
      try {
        const stale = await reg.pushManager.getSubscription();
        if (stale) await stale.unsubscribe();
      } catch {
        // Ignore — best-effort cleanup
      }
      // Brief pause to let the push service settle after unsubscribe
      await new Promise((r) => setTimeout(r, 500));
      try {
        return await doSubscribe();
      } catch (retryErr) {
        console.error('[Push] Subscribe retry failed:', retryErr);
        return null;
      }
    }
    console.error('[Push] Subscribe failed:', err);
    return null;
  }
}

/**
 * Get current push subscription from the service worker (if any).
 */
export async function getCurrentSubscription(): Promise<PushSubscriptionJSON | null> {
  const reg = await getSWRegistration();
  if (!reg) return null;
  try {
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return null;
    const json = sub.toJSON();
    return {
      endpoint: json.endpoint!,
      keys: {
        p256dh: json.keys!.p256dh!,
        auth: json.keys!.auth!,
      },
      expirationTime: json.expirationTime ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Unsubscribe from push (local only). Call unsubscribeFromBackend separately to remove from server.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  const reg = await getSWRegistration();
  if (!reg) return false;
  try {
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
    return true;
  } catch {
    return false;
  }
}

/**
 * Send subscription to backend (requires auth). Call after subscribeToPush().
 */
export async function sendSubscriptionToBackend(
  subscription: PushSubscriptionJSON,
  options?: { userAgent?: string; deviceType?: 'desktop' | 'mobile' | 'tablet' }
): Promise<void> {
  await apiService.post('/push/subscribe', {
    subscription: {
      endpoint: subscription.endpoint,
      keys: subscription.keys,
    },
    userAgent: options?.userAgent ?? navigator.userAgent,
    deviceType: options?.deviceType,
  });
}

/**
 * Remove subscription from backend by endpoint.
 */
export async function unsubscribeFromBackend(endpoint: string): Promise<void> {
  await apiService.delete('/push/unsubscribe', { data: { endpoint } });
}

/**
 * Get user's subscriptions from backend (for settings).
 */
export async function getSubscriptionsFromBackend(): Promise<
  { id: string; endpoint: string; deviceType: string | null; createdAt: string }[]
> {
  const res = await apiService.get<{ subscriptions: { id: string; endpoint: string; deviceType: string | null; createdAt: string }[] }>('/push/subscription');
  return res.data?.subscriptions ?? [];
}

/**
 * Full flow: request permission, subscribe, and send to backend (landlord app).
 * In PWA standalone, retries subscription once after a delay if SW wasn't ready.
 * Returns true if subscription was registered successfully.
 */
export async function enablePushNotifications(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const permission = await requestPermission();
  if (permission === 'denied') {
    setStoredPushChoice('denied');
    return false;
  }
  if (permission !== 'granted') return false;

  let subscription = await subscribeToPush();
  if (!subscription && isStandalonePWA()) {
    await new Promise((r) => setTimeout(r, 2000));
    subscription = await subscribeToPush();
  }
  if (!subscription) {
    setPushSubscriptionFailed(true);
    return false;
  }

  try {
    await sendSubscriptionToBackend(subscription);
    setStoredPushChoice('granted');
    setPushSubscriptionFailed(false);
    return true;
  } catch (err) {
    console.error('[Push] Failed to register with backend:', err);
    setPushSubscriptionFailed(true);
    return false;
  }
}

/**
 * Full flow for tenants: request permission, subscribe, register with tenant backend.
 * In PWA standalone, retries subscription once after a delay (SW may not be ready on first tap).
 * Returns true if subscription was registered successfully.
 */
export async function enableTenantPushNotifications(tenantId: string): Promise<boolean> {
  if (!isPushSupported()) return false;
  const permission = await requestPermission();
  if (permission === 'denied') {
    setStoredPushChoice('denied');
    return false;
  }
  if (permission !== 'granted') return false;

  let subscription = await subscribeToPush();
  // PWA standalone: SW often not ready on first tap; retry after a short wait
  if (!subscription && isStandalonePWA()) {
    await new Promise((r) => setTimeout(r, 2000));
    subscription = await subscribeToPush();
  }
  if (!subscription) {
    setPushSubscriptionFailed(true);
    return false;
  }

  try {
    await tenantService.subscribePush(tenantId, {
      endpoint: subscription.endpoint,
      keys: subscription.keys,
      userAgent: navigator.userAgent,
      deviceType: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    });
    setStoredPushChoice('granted');
    setPushSubscriptionFailed(false);
    return true;
  } catch (err) {
    console.error('[Push] Tenant push register failed:', err);
    setPushSubscriptionFailed(true);
    return false;
  }
}
